import {
  GetVideoStreamBlockQuery,
  KineticModule,
  PrivilegeType,
  RecordingType,
  ScreenFragment,
  useGetVideoStreamBlockQuery,
  useVideoStreamBlockUpdatedSubscription,
  useVideoStreamUnblockedSubscription,
  VideoStreamAccessType,
} from '@kinetic-ui/data-layer';
import { Circle, Close, CloseFullscreen, OpenInFull, PublicOffRounded } from '@mui/icons-material';
import { IconButton, MenuItem, Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import { useFullscreenVideoPlayer } from 'contexts';
import { useAllPrivileges, useDateTimeFormatPreference } from 'kineticUtils';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CameraSelectionContext } from 'components/ExtendedVideoPlayer/context';
import ScreenLayout from 'components/ScreenLayout';
import { timeZoneState, useLocalizer } from 'components/TaskScheduler';
import { DISPLAY_DATE_REPORT_FORMAT } from 'const';
import { JoystickContext } from 'contexts';
import { UserContext } from 'contexts/UserContext';
import L from 'leaflet';
import { useRecoilValue } from 'recoil';
import { VideoPlayerFunctionality } from 'types/videoPlayerTypes';
import useCameraDetailsOverlayStyles from '../styles';
import { useTogglePTZLock } from '../useTogglePTZLock';
import { BlockRequest } from './BlockPublicStreamPopover';
import BlockPublicStreamPopover from './BlockPublicStreamPopover/BlockPublicStreamPopover';
import CameraControls from './CameraControls';
import CameraIrisFocusControls from './CameraIrisFocusControls/CameraIrisFocusControls';
import { useCameraOverlayMutations } from './hooks';
import LocationLabel from './LocationLabel/LocationLabel';
import LockOverrideDialog from './LockOverrideDialog';
import PresetDialog from './PresetDialog';
import PresetItem from './PresetItem';
import { CameraOverlayProps } from './types';

const RecordingChips: React.FC<{
  liveRecordings: {
    id: string;
    recordingName?: string | null;
  }[];
}> = (props) => {
  const classes = useCameraDetailsOverlayStyles();
  const { t } = useTranslation(['video']);
  const { liveRecordings } = props;
  if (!liveRecordings) {
    return null;
  }
  return (
    <>
      {liveRecordings.map((record) => {
        const isShort = record.recordingName === 'short';
        return (
          <Tooltip
            key={record.id}
            title={isShort ? t('adhocRecordingProgress') : t(record.recordingName)}
          >
            <div className={clsx(classes.recordCountBadge)}>{!isShort && record.recordingName}</div>
          </Tooltip>
        );
      })}
    </>
  );
};

const CameraOverlay: React.FunctionComponent<CameraOverlayProps> = (props) => {
  const classes = useCameraDetailsOverlayStyles();
  const { openFullscreen } = useFullscreenVideoPlayer();

  const { t } = useTranslation(['video', 'common']);
  const { id: userId } = useContext(UserContext);
  const [showControls, setShowControls] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showCameraInformation, setShowCameraInformation] = useState(false);
  const [showPresetDialog, setShowPresetDialog] = useState(false);
  const [showLockOverrideDialog, setShowLockOverrideDialog] = useState(false);
  const cameraInformationRef = useRef<HTMLDivElement>(null);
  const screenLayoutRef = useRef<HTMLDivElement>(null);
  const cameraInformationButtonRef = useRef<HTMLButtonElement>(null);
  const screenLayoutButtonRef = useRef<HTMLButtonElement>(null);
  const controlsButtonRef = useRef<HTMLButtonElement>(null);
  const buttonsWrapperRef = useRef<HTMLDivElement>(null);
  const currentTimeout = useRef<NodeJS.Timeout>();
  const { selectedCameraId } = useContext(CameraSelectionContext);
  const { hasJoystick } = useContext(JoystickContext);
  const hasConfigurePrivilege = useAllPrivileges([PrivilegeType.Configure], KineticModule.Video);
  const hasUpdatePrivilege = useAllPrivileges([PrivilegeType.Update], KineticModule.Video);
  const hasCreatePrivilege = useAllPrivileges([PrivilegeType.Create], KineticModule.Video);
  const hasManageRecordingsPrivilege = useAllPrivileges(
    [PrivilegeType.ManageRecordings],
    KineticModule.Video
  );
  const [isCameraLockOverridden, setIsCameraLockOverridden] = useState(false);

  const [showScreenList, setShowScreenList] = useState(false);

  const presetMenuRef = useRef<HTMLDivElement>(null);
  const presetMenuButtonRef = useRef<HTMLButtonElement>(null);
  const controlsBoxRef = useRef<HTMLDivElement>(null);

  const [selectedScreenLayout, setSelectedScreenLayout] = useState<ScreenFragment | null>(null);
  const [showScreenLayoutDialog, setShowScreenLayoutDialog] = useState(false);

  const publicButtonRef = useRef<HTMLButtonElement>(null);
  const blockPopoverRef = useRef<HTMLDivElement>(null);
  const [showBlockPopover, setShowBlockPopover] = useState(false);

  const {
    data,
    removeFromLayout,
    tour,
    goFullScreen,
    exitFullScreen,
    hideWhenDragged = false,
    refetchData,
    videoPlayerFunctionalities,
    addonsRight,
    containerSize,
  } = props;

  const controlSize = useMemo(() => {
    if (controlsBoxRef.current) {
      return {
        width: controlsBoxRef.current.clientWidth,
        height: controlsBoxRef.current.clientHeight,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlsBoxRef.current?.clientWidth, controlsBoxRef.current?.clientHeight]);

  const { toggleIsPublic, savePreset, gotoPreset, blockPublicKvdsStream, unblockPublicKvdsStream } =
    useCameraOverlayMutations(data?.id, refetchData);

  const { togglePTZLock } = useTogglePTZLock(data, refetchData);

  const [isFullScreen, setIsFullScreen] = useState(false);

  const [camera, setCamera] = useState(data);

  useEffect(() => {
    const updatedCamera = data;
    if (
      camera?.ptzLock?.isLocked &&
      camera.ptzLock.userId === userId &&
      updatedCamera?.ptzLock?.isLocked &&
      updatedCamera.ptzLock.userId !== userId
    ) {
      setIsCameraLockOverridden(true);
    }
    setCamera(updatedCamera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, userId]);

  useEffect(() => {
    isCameraLockOverridden && setShowLockOverrideDialog(true);
  }, [isCameraLockOverridden]);

  const hasPTZOverrideLockPrivilege = useAllPrivileges(
    [PrivilegeType.PtzOverrideLock],
    KineticModule.Video
  );

  const closeFullscreen = () => {
    !document.fullscreenElement && setIsFullScreen(false);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', closeFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', closeFullscreen);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      currentTimeout.current && clearTimeout(currentTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOutsideControls = useCallback(
    (event: any) => {
      showPresets &&
        presetMenuRef.current &&
        !presetMenuRef.current.contains(event.target) &&
        presetMenuButtonRef.current &&
        !presetMenuButtonRef.current.contains(event.target) &&
        setShowPresets(false);

      showCameraInformation &&
        cameraInformationRef.current &&
        !cameraInformationRef.current.contains(event.target) &&
        cameraInformationButtonRef.current &&
        !cameraInformationButtonRef.current.contains(event.target) &&
        setShowCameraInformation(false);

      showScreenList &&
        screenLayoutRef.current &&
        !screenLayoutRef.current.contains(event.target) &&
        screenLayoutButtonRef.current &&
        !screenLayoutButtonRef.current.contains(event.target) &&
        setShowScreenList(false);
    },
    [showCameraInformation, showPresets, showScreenList]
  );

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutsideControls);
    return () => {
      window.removeEventListener('mousedown', handleClickOutsideControls);
    };
  }, [handleClickOutsideControls, presetMenuRef, cameraInformationRef, screenLayoutRef]);

  const renderPresets = () => {
    const presets = data.presets;
    if (showPresets) {
      return (
        <div ref={presetMenuRef} className={classes.presetMenuWrapper}>
          <div
            className={classes.presetItemsWrapper}
            onWheel={(event) => {
              L.DomEvent.stopPropagation(event as unknown as Event);
              event.stopPropagation();
            }}
          >
            {presets?.map((preset, index) => (
              <PresetItem
                key={preset.id}
                preset={preset}
                index={index}
                savePreset={savePreset}
                handleClosePresetMenu={() => setShowPresets(false)}
                gotoPreset={gotoPreset}
                cameraName={data.name}
                hasConfigurePrivilege={hasConfigurePrivilege}
                hasUpdatePrivilege={hasUpdatePrivilege}
                hasCreatePrivilege={hasCreatePrivilege}
              />
            ))}
          </div>

          <div className={classes.saveCurrentItem}>
            <MenuItem
              className={classes.saveCurrentItem}
              classes={{ root: classes.saveCurrentItemRoot }}
              onClick={(event) => {
                L.DomEvent.stopPropagation(event as unknown as Event);
                event.stopPropagation();
                setShowPresetDialog(true);
                setShowPresets(false);
              }}
            >
              <Typography
                variant='subtitle2'
                className={clsx(classes.presetItemText, classes.menuItemText)}
              >
                {t('common:saveCurrent')}
              </Typography>
            </MenuItem>
          </div>
        </div>
      );
    }
  };

  const renderCameraInformation = () => {
    if (showCameraInformation) {
      return (
        <div ref={cameraInformationRef} className={classes.cameraInformationWrapper}>
          <MenuItem>
            <Typography
              variant='subtitle2'
              className={clsx(classes.menuItemText, classes.lastCommandText)}
            >
              {`${t('common:lastCommand')}:`}
            </Typography>
            <div>
              <Typography variant='subtitle2' className={classes.menuItemText}>
                {data.ptzLock?.fullName || ''}
              </Typography>
              <Typography variant='subtitle2' className={classes.menuItemText}>
                {data.ptzLock?.lastUsedTime
                  ? moment(data.ptzLock.lastUsedTime).format(DISPLAY_DATE_REPORT_FORMAT)
                  : ''}
              </Typography>
            </div>
          </MenuItem>
        </div>
      );
    }
  };

  const handleToggleIsPublic = () => {
    const streamManagerId = data.streams?.filter(
      (stream) => stream.accessType === VideoStreamAccessType.Public
    )[0]?.streamManager;
    if (!streamManagerId) {
      return;
    }
    return toggleIsPublic(data.id, streamManagerId, !data.publicFeedEnabled);
  };

  const isPublicFeedOff = useMemo(() => {
    const streamManagerId = data.streams?.filter(
      (stream) => stream.accessType === VideoStreamAccessType.Public
    )[0]?.streamManager;

    if (!streamManagerId) {
      return false;
    }

    return !data.publicFeedEnabled;
  }, [data]);

  const renderPresetDialog = useCallback(() => {
    return (
      <PresetDialog
        open={showPresetDialog}
        handleClosePresetDialog={() => setShowPresetDialog(false)}
        savePreset={savePreset}
        cameraName={data.name}
        hasConfigurePrivilege={hasConfigurePrivilege}
        hasUpdatePrivilege={hasUpdatePrivilege}
        hasCreatePrivilege={hasCreatePrivilege}
      />
    );
  }, [
    data.name,
    hasConfigurePrivilege,
    hasCreatePrivilege,
    hasUpdatePrivilege,
    savePreset,
    showPresetDialog,
  ]);

  const handleCloseLockOverrideDialog = useCallback(() => {
    isCameraLockOverridden && setIsCameraLockOverridden(false);
    setShowLockOverrideDialog(false);
  }, [isCameraLockOverridden]);

  const renderLockOverrideDialog = useCallback(() => {
    return (
      <LockOverrideDialog
        open={showLockOverrideDialog}
        camera={data}
        isOverridden={isCameraLockOverridden}
        handleCloseDialog={handleCloseLockOverrideDialog}
        lockPTZ={togglePTZLock}
        hasPTZOverrideLockPrivilege={hasPTZOverrideLockPrivilege}
      />
    );
  }, [
    data,
    handleCloseLockOverrideDialog,
    hasPTZOverrideLockPrivilege,
    isCameraLockOverridden,
    showLockOverrideDialog,
    togglePTZLock,
  ]);

  const handleOpenFullScreen = () => {
    openFullscreen(camera);
  };

  const hasCameraControls = useMemo(
    () =>
      videoPlayerFunctionalities.some((v) =>
        [
          VideoPlayerFunctionality.IRIS_CONTROL,
          VideoPlayerFunctionality.CAPABILITIES,
          VideoPlayerFunctionality.FOCUS_CONTROL,
          VideoPlayerFunctionality.DETECTIONS,
        ].includes(v)
      ),
    [videoPlayerFunctionalities]
  );

  const renderScreenLayout = () => {
    if (showScreenList) {
      return (
        <div ref={screenLayoutRef} className={classes.screenLayoutWrapper}>
          <ScreenLayout.ScreenLayoutList
            onScreenSelect={(screen) => {
              setSelectedScreenLayout(screen);
              setShowScreenLayoutDialog(true);
              setShowScreenList(false);
            }}
            cameraHeight={containerSize?.height!}
            controllerHeight={controlSize?.height!}
          />
        </div>
      );
    }
  };

  const renderScreenSelectModel = () => {
    return (
      <ScreenLayout.ScreenLayoutsModel
        screen={selectedScreenLayout}
        showDialog={showScreenLayoutDialog}
        setShowDialog={() => {
          setShowScreenLayoutDialog(false);
          setSelectedScreenLayout(null);
        }}
        cameraName={data}
      />
    );
  };

  const recordingInProgress = useMemo(() => {
    if (!data.liveRecordings) {
      return false;
    }
    const manualRecordings = data.liveRecordings.filter(
      (recording) => recording.recordingType === RecordingType.Manual
    );
    return manualRecordings.length > 0;
  }, [data]);

  const hasKvdsPublicStream = useMemo(
    () =>
      data.streams?.some(
        (s) => s?.accessType === VideoStreamAccessType.Public && s?.streamType === 'KVDS'
      ) ?? false,
    [data.streams]
  );

  const { data: blockData, refetch: refetchVideoStreamBlock } = useGetVideoStreamBlockQuery({
    variables: { cameraId: data.id },
    skip: !hasKvdsPublicStream,
    fetchPolicy: 'cache-and-network',
  });
  type CurrentBlock = NonNullable<GetVideoStreamBlockQuery['videoStreamBlocked']>;
  const [currentBlock, setCurrentBlock] = React.useState<CurrentBlock | null>(null);
  React.useEffect(() => {
    if (!hasKvdsPublicStream) {
      return;
    }
    setCurrentBlock(blockData?.videoStreamBlocked ?? null);
  }, [hasKvdsPublicStream, blockData?.videoStreamBlocked]);

  useVideoStreamBlockUpdatedSubscription({
    skip: !hasKvdsPublicStream,
    onData: (options) => {
      const updated = options.data.data?.videoStreamBlockUpdated;
      if (updated?.cameraId === data.id) {
        setCurrentBlock(updated);
        refreshBlock();
      }
    },
  });

  useVideoStreamUnblockedSubscription({
    skip: !hasKvdsPublicStream,
    onData: (options) => {
      const unblocked = options.data.data?.videoStreamUnblocked;
      if (unblocked?.cameraId === data.id) {
        setCurrentBlock(null);
        refreshBlock();
      }
    },
  });

  const refreshBlock = React.useCallback(() => {
    return refetchVideoStreamBlock({ cameraId: data.id });
  }, [data.id, refetchVideoStreamBlock]);

  const handleBlockSubmit = React.useCallback(
    (req: BlockRequest) => {
      return blockPublicKvdsStream(data.id, req).then(() => {
        refetchData?.();
        return refetchVideoStreamBlock();
      });
    },
    [blockPublicKvdsStream, data.id, refetchData, refetchVideoStreamBlock]
  );

  const handleUnblock = React.useCallback(() => {
    return unblockPublicKvdsStream(data.id).then(() => {
      refetchData?.();
      return refetchVideoStreamBlock();
    });
  }, [data.id, refetchData, refetchVideoStreamBlock, unblockPublicKvdsStream]);

  const renderBlockPublicStream = () => {
    if (!showBlockPopover) {
      return null;
    }
    return (
      <BlockPublicStreamPopover
        open={showBlockPopover}
        onClose={() => setShowBlockPopover(false)}
        onSubmit={handleBlockSubmit}
        cameraLabel={data?.name}
        containerEl={buttonsWrapperRef.current}
        anchorEl={publicButtonRef.current}
        panelRef={blockPopoverRef}
        currentBlock={currentBlock}
        onUnblock={handleUnblock}
      />
    );
  };

  const isKvdsBlocked = Boolean(currentBlock);
  useLocalizer();
  const tz = useRecoilValue(timeZoneState);
  const { userDateTimeFormat } = useDateTimeFormatPreference();

  const kvdsUnblockTooltip = React.useMemo(() => {
    if (!currentBlock || !tz) {
      return undefined;
    }
    const candidateIso = currentBlock.blockedUntil?.trim() || currentBlock.endsAt?.trim() || null;
    if (!candidateIso) {
      return t('publicStreamBlockedIndefinitely');
    }
    const endsAtLocal = moment.tz(candidateIso, tz);
    if (!endsAtLocal.isValid() || endsAtLocal.isBefore(moment.tz(tz))) {
      return undefined;
    }

    return `${t('unblocksAt')} ${endsAtLocal.format(userDateTimeFormat)}`;
  }, [currentBlock, tz, userDateTimeFormat, t]);

  const kvdsTooltip = React.useMemo(() => {
    if (!isKvdsBlocked) {
      return undefined;
    }
    if (currentBlock?.serverConfirmed) {
      return kvdsUnblockTooltip;
    }
    return t('publicStreamBlockPending');
  }, [isKvdsBlocked, currentBlock, kvdsUnblockTooltip, t]);

  const renderCameraOverlay = () => {
    return (
      <>
        {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PRESET_CONTROL) &&
          renderPresetDialog()}
        {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.CAMERA_LOCK) &&
          renderLockOverrideDialog()}
        <LocationLabel data={data} buttonsWrapperRef={buttonsWrapperRef} />
        {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PUBLIC_FEED) &&
          (isPublicFeedOff || isKvdsBlocked) && (
            <div
              className={clsx(
                classes.publicOffButton,
                isKvdsBlocked
                  ? currentBlock?.serverConfirmed
                    ? classes.publicOffConfirmed
                    : classes.publicOffPending
                  : classes.publicOffConfirmed
              )}
            >
              <Tooltip
                placement='left'
                title={kvdsTooltip}
                PopperProps={{ container: buttonsWrapperRef.current }}
              >
                <span>
                  <PublicOffRounded
                    fontSize='medium'
                    onClick={isKvdsBlocked ? undefined : handleToggleIsPublic}
                    style={{ cursor: isKvdsBlocked ? 'default' : 'pointer' }}
                  />
                </span>
              </Tooltip>
            </div>
          )}
        {!hideWhenDragged && (
          <div
            className={clsx(classes.videoControllsWrapper, {
              [classes.videoControllsWrapperSelected]: data?.id === selectedCameraId && hasJoystick,
            })}
            draggable={false}
            ref={buttonsWrapperRef}
          >
            {hasCameraControls && (
              <CameraIrisFocusControls
                showControls={showControls}
                setShowControls={setShowControls}
                data={data}
                controlsButtonRef={controlsButtonRef}
                isFullScreen={isFullScreen}
                videoPlayerFunctionalities={videoPlayerFunctionalities}
                refetchData={refetchData}
              />
            )}
            {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.SCREEN_LAYOUT_CONTROL) &&
              renderScreenLayout()}
            {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PRESET_CONTROL) &&
              renderPresets()}
            {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.PUBLIC_FEED) &&
              renderBlockPublicStream()}
            {renderCameraInformation()}
            <Typography
              variant='body1'
              className={clsx(classes.videoControllsTitle, {
                [classes.videoControllsWrapperSelected]:
                  data?.id === selectedCameraId && hasJoystick,
              })}
            >
              {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.RECORDING) && (
                <RecordingChips liveRecordings={data.liveRecordings!} />
              )}
              {videoPlayerFunctionalities.includes(VideoPlayerFunctionality.RECORDING) &&
                hasManageRecordingsPrivilege &&
                recordingInProgress && (
                  <Circle
                    className={clsx(classes.recordingButton, classes.smallRecordingButton)}
                    fontSize='small'
                  />
                )}
              {data.name}
            </Typography>
            <div className={classes.videoControllsButtonsWrapper} ref={controlsBoxRef}>
              {showScreenLayoutDialog && renderScreenSelectModel()}
              <CameraControls
                {...props}
                buttonsWrapperRef={buttonsWrapperRef}
                cameraInformationButtonRef={cameraInformationButtonRef}
                controlsButtonRef={controlsButtonRef}
                presetMenuButtonRef={presetMenuButtonRef}
                screenLayoutButtonRef={screenLayoutButtonRef}
                setShowCameraInformation={setShowCameraInformation}
                setShowControls={setShowControls}
                setShowLockOverrideDialog={setShowLockOverrideDialog}
                setShowPresets={setShowPresets}
                setShowScreenList={setShowScreenList}
                handleOpenFullScreen={handleOpenFullScreen}
                publicButtonRef={publicButtonRef}
                setShowBlockPopover={setShowBlockPopover}
                currentBlock={currentBlock}
                hasKvdsPublicStream={hasKvdsPublicStream}
                refetchVideoStreamBlock={refetchVideoStreamBlock}
              />
              {!isFullScreen && goFullScreen && (
                <Tooltip
                  placement='top'
                  title={t('common:fullscreen')}
                  PopperProps={{ container: buttonsWrapperRef.current }}
                >
                  <IconButton
                    size='small'
                    onClick={() => {
                      goFullScreen();
                      setIsFullScreen(true);
                    }}
                  >
                    <OpenInFull className={classes.videoControllsButton} fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              {isFullScreen && exitFullScreen && (
                <Tooltip
                  placement='top'
                  title={t('common:exitFullscreen')}
                  PopperProps={{ container: buttonsWrapperRef.current }}
                >
                  <IconButton
                    size='small'
                    onClick={() => {
                      exitFullScreen();
                    }}
                  >
                    <CloseFullscreen className={classes.videoControllsButton} fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              {addonsRight}
              {removeFromLayout && (
                <Tooltip
                  placement='top'
                  title={t('common:remove')}
                  PopperProps={{ container: buttonsWrapperRef.current }}
                >
                  <IconButton
                    size='small'
                    onClick={() => removeFromLayout(tour ? tour.id : data.id)}
                  >
                    <Close className={classes.videoControllsButton} fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return renderCameraOverlay();
};

export default CameraOverlay;
